import React, { useEffect, useState } from 'react';
import { ProjectShare, ProjectPermissions, UserProfile, useKanbanStore } from '../../store/useKanbanStore';

interface SharingPanelProps {
  isOpen: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
}

type ShareDraft = ProjectShare & { permissions: ProjectPermissions };

const emptyPermissions = (): ProjectPermissions => ({
  canEditTasks: false,
  canEditSubtasks: false,
  canComment: false,
});

const PermissionChecks: React.FC<{
  permissions: ProjectPermissions;
  onChange: (permissions: ProjectPermissions) => void;
}> = ({ permissions, onChange }) => {
  const update = (key: keyof ProjectPermissions, value: boolean) => onChange({ ...permissions, [key]: value });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
      <label className="flex items-center gap-1.5 whitespace-nowrap">
        <input type="checkbox" checked={permissions.canEditTasks} onChange={(event) => update('canEditTasks', event.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
        Edit Tasks
      </label>
      <label className="flex items-center gap-1.5 whitespace-nowrap">
        <input type="checkbox" checked={permissions.canEditSubtasks} onChange={(event) => update('canEditSubtasks', event.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
        Edit Subtasks
      </label>
      <label className="flex items-center gap-1.5 whitespace-nowrap">
        <input type="checkbox" checked={permissions.canComment} onChange={(event) => update('canComment', event.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
        Comment
      </label>
      {!permissions.canEditTasks && !permissions.canEditSubtasks && !permissions.canComment && (
        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">View Only</span>
      )}
    </div>
  );
};

const EMPTY_SHARES: ProjectShare[] = [];

export const SharingPanel: React.FC<SharingPanelProps> = ({ isOpen, projectId, projectName, onClose }) => {
  const shares = useKanbanStore((state) => state.projectShares[projectId] || EMPTY_SHARES);
  const fetchShares = useKanbanStore((state) => state.fetchShares);
  const searchUsers = useKanbanStore((state) => state.searchUsers);
  const shareProject = useKanbanStore((state) => state.shareProject);
  const unshareProject = useKanbanStore((state) => state.unshareProject);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<UserProfile[]>([]);
  const [pending, setPending] = useState<ShareDraft[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    void fetchShares(projectId);
    setQuery('');
    setMatches([]);
    setPending([]);
  }, [isOpen, projectId, fetchShares]);

  useEffect(() => {
    if (!isOpen || !query.trim()) {
      setMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchUsers(query);
        if (!cancelled) setMatches(results.filter((user) => !shares.some((share) => share.userId === user.id) && !pending.some((invite) => invite.userId === user.id)));
      } catch {
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, query, searchUsers, shares, pending]);

  if (!isOpen) return null;

  const addPending = (user: UserProfile) => {
    setPending((current) => [...current, { userId: user.id, username: user.username, permissions: emptyPermissions() }]);
    setQuery('');
    setMatches([]);
  };

  const invite = async (draft: ShareDraft) => {
    setBusyUserId(draft.userId);
    try {
      await shareProject(projectId, draft.userId, draft.permissions);
      setPending((current) => current.filter((entry) => entry.userId !== draft.userId));
    } finally {
      setBusyUserId(null);
    }
  };

  const updateShared = async (share: ProjectShare, permissions: ProjectPermissions) => {
    setBusyUserId(share.userId);
    try {
      await shareProject(projectId, share.userId, permissions);
    } finally {
      setBusyUserId(null);
    }
  };

  const removeShare = async (userId: string) => {
    setBusyUserId(userId);
    try {
      await unshareProject(projectId, userId);
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-0 sm:p-4 sm:backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Share Board">
      <div className="flex h-full w-full sm:h-auto sm:max-h-[90vh] sm:w-auto sm:max-w-2xl flex-col overflow-hidden rounded-none sm:rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-4 sm:px-6 dark:border-slate-800 dark:bg-slate-950">
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Share Board</h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Invite people to {projectName}</p>
          </div>
          <button type="button" onClick={onClose} className="text-lg font-bold text-slate-400 hover:text-slate-700 dark:hover:text-slate-100" aria-label="Close sharing panel">✕</button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto p-3 sm:p-6">
          <section>
            <label htmlFor="share-user-search" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Invite a user</label>
            <div className="relative">
              <input id="share-user-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by username…" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              {(query.trim() || isSearching) && matches.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                  {matches.map((user) => <button key={user.id} type="button" onClick={() => addPending(user)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"><span>{user.username}</span>{user.isAdmin && <span className="text-[10px] font-semibold text-indigo-500">Admin</span>}</button>)}
                </div>
              )}
            </div>
          </section>

          {pending.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Pending invites</h3>
              {pending.map((draft) => (
                <div key={draft.userId} className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-950/30">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{draft.username}</span>
                    <button type="button" disabled={busyUserId === draft.userId} onClick={() => void invite(draft)} className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-500 disabled:opacity-50">{busyUserId === draft.userId ? 'Inviting…' : 'Invite'}</button>
                  </div>
                  <div className="mt-2"><PermissionChecks permissions={draft.permissions} onChange={(permissions) => setPending((current) => current.map((entry) => entry.userId === draft.userId ? { ...entry, permissions } : entry))} /></div>
                </div>
              ))}
            </section>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">People with access</h3>
            {shares.length === 0 ? <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700">No one else has access yet.</p> : <div className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">{shares.map((share) => <div key={share.userId} className="space-y-2 bg-white p-3 dark:bg-slate-900"><div className="flex items-center justify-between gap-3"><span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{share.username}</span><button type="button" disabled={busyUserId === share.userId} onClick={() => void removeShare(share.userId)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30" aria-label={`Remove ${share.username}`} title="Remove access">✕</button></div><PermissionChecks permissions={share.permissions} onChange={(permissions) => void updateShared(share, permissions)} /></div>)}</div>}
          </section>
        </div>

        <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-800 dark:bg-slate-950"><button type="button" onClick={onClose} className="rounded-md bg-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">Done</button></footer>
      </div>
    </div>
  );
};
